import { useState } from "react";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { ShowreelProvider } from "@/components/site/Showreel";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Footer } from "@/components/site/Footer";
import { FestivalHeader } from "@/components/site/FestivalHeader";
import { JoinDialog } from "@/components/site/JoinDialog";
import { Modal, ModalScroll } from "@/components/site/Modal";
import { PhotoMasonry, Lightbox } from "@/components/site/PhotoMasonry";
import { resolveTheme } from "@/lib/festivalTheme";
import { resolveUrl, isUploadedVideo, getEmbedUrl } from "@/lib/media";

const galleryUrls = (g) => (g || []).map((x) => (typeof x === "string" ? x : x?.url)).filter(Boolean);

const NewsDetailModal = ({ item, onClose }) => {
  const [box, setBox] = useState(null);
  if (!item) return null;
  const imgs = galleryUrls(item.gallery);
  const paras = String(item.content || "").split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
  return (
    <Modal open={!!item} onClose={onClose} size="xl" testid="festival-news-modal">
      <ModalScroll>
        <div className="p-6 md:p-10 max-w-3xl mx-auto">
          <div className="text-[11px] uppercase text-electric tracking-[0.25em] font-semibold">{item.date}{item.edition ? ` · Édition ${item.edition}` : ""}</div>
          <h2 className="font-serif text-3xl md:text-4xl text-white mt-2 mb-5">{item.title}</h2>
          {paras.length > 0 && <div className="space-y-4 text-white/75 font-light leading-relaxed mb-8">{paras.map((p, i) => <p key={i}>{p}</p>)}</div>}

          {item.mediaType === "video" && item.videoUrl && (
            <div className="relative aspect-video rounded-lg overflow-hidden border border-white/10 bg-black" data-testid="festival-news-video">
              {isUploadedVideo(item.videoUrl)
                ? <video src={resolveUrl(item.videoUrl)} controls playsInline className="w-full h-full object-contain" />
                : <iframe src={getEmbedUrl(item.videoUrl)} title={item.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />}
            </div>
          )}

          {item.mediaType === "album" && imgs.length > 0 && (
            <div data-testid="festival-news-album">
              <PhotoMasonry images={imgs} onOpen={(i) => setBox(i)} />
              <Lightbox images={imgs} index={box} onClose={() => setBox(null)} />
            </div>
          )}

          {item.mediaType === "image" && (item.cover || imgs[0]) && (
            <button onClick={() => setBox(0)} className="block w-full cursor-zoom-in" data-testid="festival-news-image">
              <img src={resolveUrl(item.cover || imgs[0])} alt={item.title} className="w-full rounded-lg border border-white/10" />
              <Lightbox images={imgs.length ? imgs : [item.cover]} index={box} onClose={() => setBox(null)} />
            </button>
          )}
        </div>
      </ModalScroll>
    </Modal>
  );
};

const NewsBody = () => {
  const { content } = useContent();
  const archive = content.festivalArchive || {};
  const eds = (archive.editions || []).filter((e) => e && !e.hidden);
  const current = eds.find((e) => e.current) || [...eds].sort((a, b) => (parseInt(b.year, 10) || 0) - (parseInt(a.year, 10) || 0))[0];
  const themeVars = resolveTheme(archive.theme, current);

  const all = (archive.news || []).filter((n) => n && n.status !== "hidden");
  const years = [...new Set(all.map((n) => n.edition).filter(Boolean))].sort((a, b) => (parseInt(b, 10) || 0) - (parseInt(a, 10) || 0));
  const [year, setYear] = useState("all");
  const items = all.filter((n) => year === "all" || String(n.edition) === String(year));
  const [sel, setSel] = useState(null);
  const [joinOpen, setJoinOpen] = useState(false);

  return (
    <div className="App festival-universe min-h-screen flex flex-col" style={{ ...themeVars, backgroundColor: "var(--fx-bg)", color: "var(--fx-text)" }}>
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <FestivalHeader brand={content.brand || {}} socials={content.contact?.socials || []} onApply={() => setJoinOpen(true)} />

      <main className="flex-1 pt-[110px] pb-20" data-testid="festival-news-page">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-electric mb-2 font-semibold">Karams Festival</div>
          <h1 className="font-serif text-4xl md:text-6xl text-white tracking-tight mb-8">Actualités</h1>

          {years.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10" data-testid="festival-news-years">
              {["all", ...years].map((y) => (
                <button key={y} onClick={() => setYear(y)} data-testid={`festival-news-year-${y}`}
                  className={`px-4 h-9 rounded-md text-[12px] tracking-[0.1em] uppercase transition-colors ${String(y) === String(year) ? "bg-electric font-semibold" : "border border-white/15 text-white/70 hover:text-electric"}`}
                  style={String(y) === String(year) ? { color: "var(--fx-on-primary)" } : undefined}>{y === "all" ? "Toutes" : y}</button>
              ))}
            </div>
          )}

          {items.length === 0 ? (
            <p className="text-secondary2 font-light">Aucune actualité pour le moment.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((n, i) => (
                <button key={n.id || i} onClick={() => setSel(n)} data-testid={`festival-news-card-${i}`}
                  className="group text-left rounded-xl overflow-hidden border border-white/10 bg-[#0c0c0e] hover:border-electric/50 transition-colors">
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#0e0e0e]">
                    {n.cover
                      ? <img src={resolveUrl(n.cover)} alt={n.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
                      : <div className="w-full h-full flex items-center justify-center text-white/25 text-sm">{n.title}</div>}
                    {n.mediaType === "album" && <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded bg-black/60 border border-white/15 text-white/80">Album</span>}
                    {n.mediaType === "video" && <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded bg-black/60 border border-white/15 text-white/80">Vidéo</span>}
                  </div>
                  <div className="p-5">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-electric mb-2">{n.date}{n.edition ? ` · ${n.edition}` : ""}</div>
                    <div className="text-lg text-white leading-snug line-clamp-2 group-hover:text-electric transition-colors">{n.title}</div>
                    {n.excerpt && <div className="text-sm text-secondary2 mt-2 line-clamp-2 font-light">{n.excerpt}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <NewsDetailModal item={sel} onClose={() => setSel(null)} />
      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} type="festival" refId="festival"
        refTitle="Karams Festival" roles={(content.festival || {}).joinRoles || []}
        notice={(content.festival || {}).participation?.notice} documents={(content.festival || {}).participation?.documents}
        heading="Soumettre ma candidature" intro="Candidatez, devenez bénévole ou partenaire du Karams Festival." />
    </div>
  );
};

export default function FestivalNewsPage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <ShowreelProvider>
          <Analytics />
          <NewsBody />
        </ShowreelProvider>
      </ReactLenis>
    </ContentProvider>
  );
}
